import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { modelMatches } from "@/lib/modelMatch";

export interface CatalogPart {
  id: string;
  name: string;
  part_number: string | null;
  brand: string | null;
  appliance_type: string;
  model_pattern: string | null;
  price: number;
  currency: string;
  notes: string | null;
}

interface RequiredRow {
  id: string;
  catalog_id: string;
  appliance_type: string;
  brand: string | null;
  model_pattern: string | null;
}

interface BindingRow {
  id: string;
  catalog_id: string;
  workflow_item_id: string;
  quantity: number;
}

const norm = (s: string | null | undefined) => (s || "").trim().toLowerCase();

/**
 * Returns the list of REQUIRED catalog parts for a given appliance.
 * Filters by appliance_type + brand + model_pattern from `spare_part_required`
 * intersected with `spare_parts_catalog`.
 */
export function useRequiredSpareParts(applianceType: string | null, brand: string | null, modelNumber: string | null) {
  return useQuery({
    queryKey: ["required-spare-parts", applianceType, brand, modelNumber],
    enabled: !!applianceType,
    queryFn: async (): Promise<CatalogPart[]> => {
      const [reqRes, catRes] = await Promise.all([
        supabase.from("spare_part_required" as any).select("*"),
        supabase.from("spare_parts_catalog" as any).select("*"),
      ]);
      if (reqRes.error) throw reqRes.error;
      if (catRes.error) throw catRes.error;
      const required = (reqRes.data || []) as unknown as RequiredRow[];
      const catalog = (catRes.data || []) as unknown as CatalogPart[];
      const catMap = new Map(catalog.map((c) => [c.id, c]));
      const out: CatalogPart[] = [];
      const seen = new Set<string>();
      for (const r of required) {
        if (norm(r.appliance_type) !== norm(applianceType)) continue;
        if (r.brand && norm(r.brand) !== norm(brand)) continue;
        if (!modelMatches(modelNumber, r.model_pattern)) continue;
        const cat = catMap.get(r.catalog_id);
        if (!cat || seen.has(cat.id)) continue;
        seen.add(cat.id);
        out.push(cat);
      }
      return out;
    },
  });
}

/**
 * Returns catalog parts bound to the given workflow_item_ids, filtered by
 * the appliance's brand and model so a step bound to multiple brand-specific
 * parts only contributes the one matching THIS appliance.
 */
export function useWorkflowStepParts(
  workflowItemIds: string[],
  brand: string | null = null,
  modelNumber: string | null = null,
) {
  const key = [...workflowItemIds].sort().join("|");
  return useQuery({
    queryKey: ["workflow-step-parts", key, norm(brand), norm(modelNumber)],
    enabled: workflowItemIds.length > 0,
    queryFn: async (): Promise<{ part: CatalogPart; quantity: number; workflow_item_id: string }[]> => {
      const { data: bindings, error: bErr } = await supabase
        .from("spare_part_workflow_bindings" as any)
        .select("*")
        .in("workflow_item_id", workflowItemIds);
      if (bErr) throw bErr;
      const rows = (bindings || []) as unknown as BindingRow[];
      if (rows.length === 0) return [];
      const catIds = [...new Set(rows.map((r) => r.catalog_id))];
      const { data: cat, error: cErr } = await supabase
        .from("spare_parts_catalog" as any)
        .select("*")
        .in("id", catIds);
      if (cErr) throw cErr;
      const catMap = new Map(((cat || []) as unknown as CatalogPart[]).map((c) => [c.id, c]));
      const nBrand = norm(brand);
      const fits = (p: CatalogPart) => {
        if (nBrand && p.brand && norm(p.brand) !== nBrand) return false;
        if (!modelMatches(modelNumber, p.model_pattern)) return false;
        return true;
      };
      // Group bindings by workflow_item_id to dedupe brand-specific variants
      const byStep = new Map<string, { part: CatalogPart; quantity: number; workflow_item_id: string }[]>();
      for (const r of rows) {
        const part = catMap.get(r.catalog_id);
        if (!part) continue;
        if (!fits(part)) continue;
        const arr = byStep.get(r.workflow_item_id) || [];
        arr.push({ part, quantity: Number(r.quantity || 1), workflow_item_id: r.workflow_item_id });
        byStep.set(r.workflow_item_id, arr);
      }
      const out: { part: CatalogPart; quantity: number; workflow_item_id: string }[] = [];
      for (const [, arr] of byStep) {
        // Prefer brand+model-specific match; fall back to first.
        const specific = arr.find((x) => x.part.brand && x.part.model_pattern);
        out.push(specific || arr[0]);
      }
      return out;
    },
  });
}

export function sumPartsPrice(parts: CatalogPart[]): number {
  return parts.reduce((acc, p) => acc + Number(p.price || 0), 0);
}

export function sumBindingsPrice(rows: { part: CatalogPart; quantity: number }[]): number {
  return rows.reduce((acc, r) => acc + Number(r.part.price || 0) * Number(r.quantity || 1), 0);
}
